/**
 * Email Templates Tab
 * Displays and manages email templates with stats cards and filters
 * Similar design to VendorManagement list page
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconTemplate,
  IconEye,
  IconEdit,
  IconTrash,
  IconPlus,
  IconToggleLeft,
  IconToggleRight,
  IconFilter,
  IconCheck,
  IconX,
  IconCalendar,
} from '@tabler/icons-react';
import {
  Stack,
  Group,
  Text,
  Button,
  ActionIcon,
  Badge,
  Card,
  Paper,
  ThemeIcon,
  TextInput,
  Select,
  Drawer,
  Divider,
  Tooltip,
  Alert,
} from '../../../../../components/ui';
import { SimpleGrid } from '../../../../../components/ui/layout';
import { DatePickerInput } from '@mantine/dates';
import { DataTable } from '../../../../../components/advanced/data';
import type { DataTableColumn } from '../../../../../components/advanced/data/DataTable';
import { EmptyState } from '../../../../../components/ui/data-display';
import { alert } from '../../../../../components/ui/providers/AlertProvider';
import type { EmailTemplatesTabProps, EmailTemplateRow } from '../types';
import { mapTemplateToRow } from '../types';
import { formatDate } from '../../types';

// Stats filter type
type StatsFilter = 'all' | 'active' | 'inactive' | null;

export function EmailTemplatesTab({
  templates,
  loading,
  onRefresh,
  onView,
  onEdit,
  onDelete,
  onCreate,
}: EmailTemplatesTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filtersDrawerOpened, setFiltersDrawerOpened] = useState(false);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [dateFromFilter, setDateFromFilter] = useState<Date | null>(null);
  const [dateToFilter, setDateToFilter] = useState<Date | null>(null);
  const [statsFilter, setStatsFilter] = useState<StatsFilter>(null);

  // Transform data for DataTable
  const rows: EmailTemplateRow[] = useMemo(() => {
    return templates.map(mapTemplateToRow);
  }, [templates]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = templates.length;
    const active = templates.filter((t) => t.isActive).length;
    const inactive = templates.filter((t) => !t.isActive).length;
    return { total, active, inactive };
  }, [templates]);

  // Helper to parse date for filtering
  const parseDate = (dateValue: any): Date | null => {
    if (!dateValue) return null;
    if (typeof dateValue === 'string') {
      return new Date(dateValue);
    }
    if (typeof dateValue === 'number') {
      const str = String(dateValue);
      if (str.length === 14) {
        const year = parseInt(str.substring(0, 4));
        const month = parseInt(str.substring(4, 6)) - 1;
        const day = parseInt(str.substring(6, 8));
        return new Date(year, month, day);
      }
    }
    return null;
  };

  // Filter data
  const filteredRows = useMemo(() => {
    let filtered = [...rows];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (row) =>
          row.templateName?.toLowerCase().includes(query) ||
          row.subject?.toLowerCase().includes(query) ||
          row.subject?.toLowerCase().includes(query)
      );
    }

    // Stats card filter
    if (statsFilter) {
      switch (statsFilter) {
        case 'active':
          filtered = filtered.filter((row) => row.isActive === true);
          break;
        case 'inactive':
          filtered = filtered.filter((row) => row.isActive === false);
          break;
        case 'all':
        default:
          break;
      }
    } else {
      // Regular status filter
      if (statusFilter) {
        filtered = filtered.filter(
          (row) => String(row.isActive) === statusFilter
        );
      }
    }

    // Date from filter
    if (dateFromFilter) {
      filtered = filtered.filter((row) => {
        const createdDate = parseDate(row.createdDateTime);
        return createdDate && createdDate >= dateFromFilter;
      });
    }

    // Date to filter
    if (dateToFilter) {
      const endOfDay = new Date(dateToFilter);
      endOfDay.setHours(23, 59, 59, 999);
      filtered = filtered.filter((row) => {
        const createdDate = parseDate(row.createdDateTime);
        return createdDate && createdDate <= endOfDay;
      });
    }

    // Default sort by ID descending (newest first)
    filtered.sort((a, b) => b.templateID - a.templateID);

    return filtered;
  }, [rows, searchQuery, statusFilter, dateFromFilter, dateToFilter, statsFilter]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilter) count++;
    if (dateFromFilter) count++;
    if (dateToFilter) count++;
    return count;
  }, [statusFilter, dateFromFilter, dateToFilter]);

  // Handle stats card click
  const handleStatsClick = useCallback((filter: StatsFilter) => {
    setStatsFilter((prev) => (prev === filter ? null : filter));
    setStatusFilter(null);
  }, []);

  // Clear all filters
  const clearAllFilters = () => {
    setStatusFilter(null);
    setDateFromFilter(null);
    setDateToFilter(null);
    setStatsFilter(null);
  };

  const hasActiveFilters =
    statusFilter || dateFromFilter || dateToFilter || statsFilter;

  // Define columns
  const columns: DataTableColumn<EmailTemplateRow>[] = useMemo(
    () => [
      {
        key: 'templateID',
        label: 'ID',
        width: 80,
        sortable: true,
        render: (value) => (
          <Text size="sm" fw={500}>
            #{value}
          </Text>
        ),
      },
      {
        key: 'templateName',
        label: 'Template Name',
        sortable: true,
        render: (value, row) => (
          <Group gap="xs">
            <ThemeIcon
              size="sm"
              variant="light"
              color={row.isActive ? 'blue' : 'gray'}
            >
              <IconTemplate size={12} />
            </ThemeIcon>
            <Stack gap={0}>
              <Text size="sm" fw={500}>
                {value || 'Unnamed Template'}
              </Text>
              {row.subject && (
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {row.subject}
                </Text>
              )}
            </Stack>
          </Group>
        ),
      },
      {
        key: 'subject',
        label: 'Subject',
        sortable: true,
        render: (value) => (
          <Text size="sm" lineClamp={1} style={{ maxWidth: 250 }}>
            {value || '(No Subject)'}
          </Text>
        ),
      },
      {
        key: 'isActive',
        label: 'Status',
        width: 120,
        sortable: true,
        filterable: true,
        render: (value) => (
          <Badge
            variant="light"
            color={value ? 'green' : 'gray'}
            leftSection={
              value ? <IconToggleRight size={12} /> : <IconToggleLeft size={12} />
            }
          >
            {value ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        key: 'createdDateTime',
        label: 'Created',
        width: 140,
        sortable: true,
        render: (value) => (
          <Text size="sm" c="dimmed">
            {formatDate(value)}
          </Text>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        width: 120,
        render: (_, row) => (
          <Group gap="xs">
            <Tooltip label="View Template">
              <ActionIcon
                variant="subtle"
                color="blue"
                onClick={(e) => {
                  e.stopPropagation();
                  onView(row);
                }}
              >
                <IconEye size={16} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Edit Template">
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(row);
                }}
              >
                <IconEdit size={16} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Delete Template">
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={async (e) => {
                  e.stopPropagation();
                  const confirmed = await alert.confirm(
                    'Delete Template',
                    `Are you sure you want to delete the template "${row.templateName}"?`,
                    {
                      confirmText: 'Delete',
                    }
                  );
                  if (confirmed) {
                    await onDelete(row.templateID);
                  }
                }}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        ),
      },
    ],
    [onView, onEdit, onDelete]
  );

  // Stats card component
  const StatsCard = ({
    icon,
    color,
    value,
    label,
    filterValue,
  }: {
    icon: React.ReactNode;
    color: string;
    value: number;
    label: string;
    filterValue: StatsFilter;
  }) => (
    <Card
      withBorder
      padding="md"
      radius="md"
      style={{
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        borderColor:
          statsFilter === filterValue
            ? `var(--mantine-color-${color}-5)`
            : undefined,
        backgroundColor:
          statsFilter === filterValue
            ? `var(--mantine-color-${color}-light)`
            : undefined,
      }}
      onClick={() => handleStatsClick(filterValue)}
    >
      <Group>
        <ThemeIcon
          size="lg"
          radius="md"
          variant={statsFilter === filterValue ? 'filled' : 'light'}
          color={color}
        >
          {icon}
        </ThemeIcon>
        <Stack gap={0}>
          <Text size="xl" fw={700}>
            {value}
          </Text>
          <Text size="xs" c="dimmed">
            {label}
          </Text>
        </Stack>
      </Group>
    </Card>
  );

  if (!loading && templates.length === 0) {
    return (
      <EmptyState
        title="No Email Templates"
        description="You haven't created any email templates yet. Create your first template to get started."
        icon={<IconTemplate size={48} />}
        actionLabel="Create Template"
        onAction={onCreate}
      />
    );
  }

  return (
    <Stack gap="lg">
      {/* Stats Cards */}
      <SimpleGrid cols={{ base: 3, md: 3 }}>
        <StatsCard
          icon={<IconTemplate size={20} />}
          color="blue"
          value={stats.total}
          label="Total Templates"
          filterValue="all"
        />
        <StatsCard
          icon={<IconCheck size={20} />}
          color="green"
          value={stats.active}
          label="Active"
          filterValue="active"
        />
        <StatsCard
          icon={<IconX size={20} />}
          color="gray"
          value={stats.inactive}
          label="Inactive"
          filterValue="inactive"
        />
      </SimpleGrid>

      {/* Active Filter Indicator */}
      {statsFilter && (
        <Alert
          color="blue"
          alertVariant="light"
          withCloseButton
          onClose={() => setStatsFilter(null)}
        >
          Showing{' '}
          <Text span fw={600}>
            {statsFilter === 'all'
              ? 'all templates'
              : `${statsFilter} templates`}
          </Text>
          . Click the card again or close to clear filter.
        </Alert>
      )}

      {/* Search and Filters Bar */}
      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" wrap="wrap">
          <Group gap="md" wrap="wrap" style={{ flex: 1 }}>
            <TextInput
              placeholder="Search by name, subject, description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ minWidth: 250 }}
              leftSection={<IconTemplate size={16} />}
            />
            <Button
              variant={activeFilterCount > 0 ? 'light' : 'default'}
              leftSection={<IconFilter size={16} />}
              onClick={() => setFiltersDrawerOpened(true)}
              color={activeFilterCount > 0 ? 'blue' : undefined}
            >
              Filters
              {activeFilterCount > 0 && (
                <Badge size="sm" variant="filled" color="blue" ml="xs">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
            {hasActiveFilters && (
              <Button
                variant="subtle"
                color="red"
                size="sm"
                leftSection={<IconTrash size={14} />}
                onClick={clearAllFilters}
              >
                Clear All
              </Button>
            )}
          </Group>
          <Button
            leftSection={<IconPlus size={16} />}
            gradientVariant="primary"
            onClick={onCreate}
          >
            Create Template
          </Button>
        </Group>
      </Paper>

      {/* Results Count */}
      <Text size="sm" c="dimmed">
        Showing {filteredRows.length} of {templates.length} templates
      </Text>

      {/* DataTable */}
      <Card withBorder p={0}>
        <DataTable
          data={filteredRows}
          columns={columns}
          loading={loading}
          onRowClick={(row) => onView(row)}
          emptyMessage="No templates found"
          striped
          highlightOnHover
          enableInternalSort
        />
      </Card>

      {/* Filters Drawer */}
      <Drawer
        opened={filtersDrawerOpened}
        onClose={() => setFiltersDrawerOpened(false)}
        title={
          <Group>
            <IconFilter size={20} />
            <Text fw={600}>Filters</Text>
          </Group>
        }
        position="right"
        size="sm"
        padding="lg"
      >
        <Stack gap="lg">
          {/* Status Filter */}
          <Select
            label="Status"
            placeholder="All statuses"
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setStatsFilter(null);
            }}
            clearable
            data={[
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' },
            ]}
            disabled={!!statsFilter}
          />

          <Divider label="Date Created" labelPosition="center" />

          {/* Date From Filter */}
          <DatePickerInput
            label="From Date"
            placeholder="Select start date"
            value={dateFromFilter}
            onChange={setDateFromFilter}
            clearable
            leftSection={<IconCalendar size={14} />}
            maxDate={dateToFilter || undefined}
          />

          {/* Date To Filter */}
          <DatePickerInput
            label="To Date"
            placeholder="Select end date"
            value={dateToFilter}
            onChange={setDateToFilter}
            clearable
            leftSection={<IconCalendar size={14} />}
            minDate={dateFromFilter || undefined}
          />

          <Divider />

          {/* Clear Filters Button */}
          <Group grow>
            <Button
              variant="light"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={clearAllFilters}
              disabled={!hasActiveFilters}
            >
              Clear All Filters
            </Button>
          </Group>

          {/* Apply Button */}
          <Button fullWidth onClick={() => setFiltersDrawerOpened(false)}>
            Apply Filters
          </Button>
        </Stack>
      </Drawer>
    </Stack>
  );
}
